/**
 * StartEase Data Room — JS-side font picker for the SignModal live preview.
 *
 * Mirrors backend/vdr_signature.py:pick_font_for_name so the modal stays
 * WYSIWYG with the PDF. Whole-name swap, never per-character mixing.
 *
 * Tier chain (matches the bundled fonts at /public/fonts/):
 *   1. "Great Vibes"          — Latin + Cyrillic basics, primary cursive
 *   2. "DejaVu Serif Italic"  — Latin-ext / Greek / Cyrillic
 *   3. "Noto Sans Devanagari" — Devanagari
 *   3. "Noto Naskh Arabic"    — Arabic (RTL; rendered right-aligned)
 */

const inRange = (code, ranges) =>
  ranges.some(([lo, hi]) => code >= lo && code <= hi);

// Script ranges that drive tier-3 selection. Mirrors backend module.
const DEVANAGARI_RANGES = [
  [0x0900, 0x097f],
  [0xa8e0, 0xa8ff],
];
const ARABIC_RANGES = [
  [0x0600, 0x06ff],
  [0x0750, 0x077f],
  [0xfb50, 0xfdff],
  [0xfe70, 0xfeff],
];

// Codepoint ranges Great Vibes is known to cover. (Latin Basic + Latin-1
// Supplement + Latin Extended-A + Cyrillic Basic + standard punctuation +
// digits + combining marks.) This is a *static* approximation of the actual
// font cmap — close enough for live preview to match the PDF without shipping
// the binary cmap to the browser.
const CURSIVE_RANGES = [
  [0x0020, 0x007e], // Basic Latin
  [0x00a0, 0x00ff], // Latin-1 Supplement
  [0x0100, 0x017f], // Latin Extended-A
  [0x0400, 0x04ff], // Cyrillic
  [0x2010, 0x205e], // General Punctuation
  [0x2000, 0x200a], // spaces
];

// Codepoint ranges DejaVu Serif Italic covers (a superset of CURSIVE_RANGES,
// minus the Indic/Arabic scripts handled by tier-3).
const DEJAVU_RANGES = [
  [0x0020, 0x007e],
  [0x00a0, 0x024f], // Latin extended
  [0x0250, 0x02af], // IPA
  [0x02b0, 0x036f], // Spacing Modifier Letters + Combining Diacriticals
  [0x0370, 0x03ff], // Greek
  [0x0400, 0x052f], // Cyrillic
  [0x2000, 0x206f], // General Punctuation
  [0x2070, 0x209f], // Sub/Super
  [0x20a0, 0x20cf], // Currency
];

export const FONT_CURSIVE = "Great Vibes";
export const FONT_FALLBACK = "DejaVu Serif Italic";
export const FONT_DEVANAGARI = "Noto Sans Devanagari";
export const FONT_ARABIC = "Noto Naskh Arabic";

// Maps a CSS font-family name (what `pickFontForName` returns) to the TTF
// basename the backend uses in its `signature.font` audit field. Keeps the
// audit row consistent with what the user actually saw rendered.
const BACKEND_FONT_NAMES = {
  [FONT_CURSIVE]: "GreatVibes-Regular",
  [FONT_FALLBACK]: "DejaVuSerif-Italic",
  [FONT_DEVANAGARI]: "NotoSansDevanagari-Regular",
  [FONT_ARABIC]: "NotoNaskhArabic-Regular",
};

export function backendFontName(cssFontFamily) {
  return BACKEND_FONT_NAMES[cssFontFamily] || "GreatVibes-Regular";
}

export function pickFontForName(name) {
  if (!name || typeof name !== "string") return FONT_CURSIVE;

  let hasDevanagari = false;
  let hasArabic = false;
  let allInCursive = true;
  let allInDejavu = true;

  for (const ch of name) {
    const code = ch.codePointAt(0);
    if (inRange(code, DEVANAGARI_RANGES)) hasDevanagari = true;
    if (inRange(code, ARABIC_RANGES)) hasArabic = true;
    if (!inRange(code, CURSIVE_RANGES)) allInCursive = false;
    if (!inRange(code, DEJAVU_RANGES)) allInDejavu = false;
  }

  if (allInCursive) return FONT_CURSIVE;
  if (hasArabic) return FONT_ARABIC;
  if (hasDevanagari) return FONT_DEVANAGARI;
  if (allInDejavu) return FONT_FALLBACK;
  return FONT_FALLBACK;
}

export function isRTLFont(font) {
  return font === FONT_ARABIC;
}
